/**
 * This provider always holds the most recent firebase user information. On every change it is updated properly.
 * Elements wrapped in the FirebaseAuthProvider tag can access the context and thus the user information.
 * This prevents us from passing the user property to all required elements and this makes it less complex.
 *
 * Whenever the value in the provider changes (i.e. the currentUser is changed), the consumers will also re-render.
 *
 * Role Structure:
 *   If a user registers itself (either via email/password self-registration or via OIDC (Google) self-registration,
 *   the following data is set in the Firebase Auth custom claims:
 *     - role: 'player'
 *     - roleScopes: []
 *     - [firstName, secondName in case of email/password self-registration]
 *
 *   If a user is registered by an admin, the following fields are set (acc. to the settings by the admin):
 *     - role: 'player' | 'admin' | ...
 *     - roleScopes: ['wako' and/or 'wkf']
 *
 *   So, after authentication, both role and role scopes can be assumed to be not undefined!
 */

import React, {useEffect, useState} from "react"
import {firebaseService} from "components/commons/firebase/Firebase"
import associationStructure from "../associationStructure/associationStructure.json";

export const FirebaseAuthContext = React.createContext()

export const FirebaseAuthProvider = ({children}) => {
    const [signedInUserAuthCompleted, setSignedInUserAuthCompleted] = useState()
    const [signedInUser, setSignedInUser] = useState();
    const [signedInUserRole, setSignedInUserRole] = useState();
    const [signedInUserRoleScopes, setSignedInUserRoleScopes] = useState();
    const [signedInUserUid, setSignedInUserUid] = useState();
    const [signedInUserEmailVerified, setSignedInUserEmailVerified] = useState();

    // Set it by default to the complete (unlimited) structure - this will only be used by the sign-up screen, where no limitation is necessary
    const [allowedAssociationStructure, setAllowedAssociationStructure] = useState();

    const [signedInUserMoodleLtiKey, setSignedInUserMoodleLtiKey] = useState();

    const [signedInUserData, setSignedInUserData] = useState()

    /**
     * Resets the user's auth data.
     * Needs to be called before sign-out.
     */
    const clearUserData = () => {
        setSignedInUser(undefined)
        setSignedInUserUid(undefined)
        setSignedInUserRole(undefined)
        setSignedInUserRoleScopes(undefined)
        setSignedInUserEmailVerified(undefined)
        setSignedInUserMoodleLtiKey(undefined)
        setSignedInUserAuthCompleted(undefined)
    }

    /**
     * Registers a listener for updating the user auth data.
     * IMPORTANT: This is async - so the "current user" will be set before the "user role".
     */
    useEffect(() => {
        firebaseService.auth().onIdTokenChanged(async (user) => {
            setSignedInUser(user)
            setSignedInUserUid(user?.uid)

            user?.getIdTokenResult()
                .then((idTokenResult) => {
                    setSignedInUserRole(idTokenResult.claims.role)
                    setSignedInUserRoleScopes(idTokenResult.claims.roleScopes)
                    setSignedInUserEmailVerified(idTokenResult.claims.email_verified)
                })
                .catch((error) => {
                    console.log(error);
                });
        });
    }, []);

    /**
     * Sets the authCompleted to true only in case the three auth states are already loaded.
     * This is important for performance and automatic routing/redirection purposes.
     */
    useEffect(() => {
        if (signedInUser !== undefined && signedInUserRole !== undefined && signedInUserEmailVerified !== undefined && signedInUserRoleScopes !== undefined && signedInUserUid !== undefined) {
            fetchSignedInUserData()
                .then(() => {
                    setAllowedAssociationStructure(getAllowedAssociationStructure(signedInUserRole, signedInUserRoleScopes))
                    setSignedInUserAuthCompleted(true)
                })
        }
    }, [signedInUserRole, signedInUserEmailVerified, signedInUser, signedInUserRoleScopes])

    const fetchSignedInUserData = async () => {
        // Under very rare circumstances - if 2 browser windows are open, the signedInUser can be null - therefore the null-safety check here
        const idToken = await signedInUser?.getIdToken()

        if (!idToken) {
            return null
        }

        return fetch('/reg-main/profile?idToken=' + idToken)
            .then(async (data) => {
                // Throw an error including the textual status of the HTTP response code (no detailed message necessary)
                if (data.status !== 200) {
                    throw new Error((await data.json()).status)
                }

                const usersJson = await data.json();
                setSignedInUserData(usersJson)
            })
            .catch(async (error) => {
                // This will lead to a re-direct to the main login page
                await firebaseService.auth().signOut()
            })
    }


    const getAllowedAssociationStructure = (signedInUserRole, signedInUserRoleScopes) => {

        if (signedInUserRole === 'player') {
            return associationStructure
        }

        const map = {}
        Object.keys(associationStructure).forEach((association) => {
            if (signedInUserRoleScopes.includes(association)) {
                map[association] = associationStructure[association]
            }
        })
        return map
    }

    const isOperationOnObjectAllowed = (object) => {
        /*
         The check does only apply for the roles organizer, contentCreator (player's UI is anyway restricted already beforehand).
         Admins are not considered here, as they must be able to modify any object within their assigned scope.
        */
        if (!object) {
            return true
        }

        if (['organizer', 'contentCreator'].includes(signedInUserRole)) {
            return object?.metadata?.creationUid === signedInUserUid
        }
        return true
    }

    return (
        <FirebaseAuthContext.Provider
            value={{
                signedInUser,
                signedInUserData,
                signedInUserAuthCompleted,
                signedInUserRole,
                signedInUserRoleScopes,
                signedInUserUid,
                signedInUserEmailVerified,
                signedInUserMoodleLtiKey,
                setSignedInUserMoodleLtiKey,
                allowedAssociationStructure,
                clearUserData,
                isOperationOnObjectAllowed
            }}>
            {children}
        </FirebaseAuthContext.Provider>
    );
};